import { useEffect, useState, type ReactNode } from 'react';

import { useHorizontalOverflow } from '../panels/useHorizontalOverflow';
import { DonutRow, SubmittedMeter } from './ExamCharts';
import { ExamHtml } from './ExamStudentSheet';
import { STATUS_COLORS, topWrongQuestions, wrongPercent, type ExamResults } from './examResults';

export interface ExamStatsTabProps {
  results: ExamResults;
  /** Spinner or retry while the questions load; the tallies wait on them. */
  questionNote: ReactNode;
}

// Báo cáo: anonymous by construction (counts, the status ring, question tallies), so it shows while projected
export function ExamStatsTab({ results, questionNote }: ExamStatsTabProps) {
  const [picked, setPicked] = useState(0);
  const count = results.questions.length;
  const { ref: chipsRef, faded: chipsFaded, measure: measureChips } = useHorizontalOverflow(count);
  const questionIndex = picked < count ? picked : 0;

  // keep the pick reachable without dragging the strip; scrollIntoView would drag the card with it
  useEffect(() => {
    const track = chipsRef.current;
    const chip = track && questionIndex < track.children.length ? track.children[questionIndex] as HTMLElement : null;
    if (!track || !chip) return;
    const right = chip.offsetLeft + chip.offsetWidth;
    if (chip.offsetLeft < track.scrollLeft) track.scrollLeft = chip.offsetLeft - 6;
    else if (right > track.scrollLeft + track.clientWidth) track.scrollLeft = right - track.clientWidth + 6;
    measureChips();
  }, [questionIndex, chipsRef, measureChips]);

  const question = results.questions[questionIndex] || null;
  const answered = question ? question.countCorrectSelections + question.countWrongSelections : 0;
  const hardest = topWrongQuestions(results.questions, 3);

  return (
    <>
      <SubmittedMeter submitted={results.submitted} total={results.total} />

      <section className="quiz-results__block">
        <h3 className="quiz-results__block-title">Tỉ lệ trạng thái làm bài</h3>
        <DonutRow slices={results.status} />
      </section>

      <section className="quiz-results__block">
        <h3 className="quiz-results__block-title">Câu hỏi sai nhiều nhất</h3>
        {questionNote}
        {hardest.length === 0 ? <p className="quiz-results__empty">Chưa có câu nào bị làm sai.</p> : hardest.map((stat) => (
          <div key={stat.id} className="quiz-results__wrong">
            <div className="quiz-results__wrong-title">{stat.title}</div>
            <div className="quiz-results__wrong-figures">
              <span className="quiz-results__wrong-count">{stat.countWrongSelections}</span>
              <span className="quiz-results__wrong-percent">{wrongPercent(stat)}%</span>
            </div>
            <div className="quiz-results__wrong-track"><span className="quiz-results__wrong-fill" style={{ width: `${wrongPercent(stat)}%` }} /></div>
            <div className="quiz-results__wrong-captions">
              <span className="quiz-results__caption">Trả lời sai</span>
              <span className="quiz-results__caption">Trên số học sinh trả lời</span>
            </div>
          </div>
        ))}
      </section>

      <section className="quiz-results__block">
        <h3 className="quiz-results__block-title">Nội dung câu hỏi</h3>
        <div className="h-scroll quiz-results__chips">
          <div className="h-scroll__track quiz-results__chip-track" ref={chipsRef} onScroll={measureChips} role="group" aria-label="Chọn câu hỏi để xem thống kê">
            {results.questions.map((item, index) => (
              <button key={item.id} type="button" className={index === questionIndex ? 'quiz-results__chip quiz-results__chip--active' : 'quiz-results__chip'} aria-pressed={index === questionIndex} onClick={() => setPicked(index)}>Câu {index + 1}</button>
            ))}
          </div>
          {chipsFaded ? <span className="h-scroll__fade" aria-hidden={true} /> : null}
        </div>
        <p className="quiz-results__stem">{question ? (question.stemHtml ? <>Câu {question.index + 1}: <ExamHtml html={question.stemHtml} /></> : question.title) : 'Bài kiểm tra này chưa có câu nào.'}</p>
        <h3 className="quiz-results__block-title quiz-results__block-title--sub">Thống kê câu trả lời</h3>
        {!question || answered + question.blank === 0 ? <p className="quiz-results__empty">Chưa có học sinh nào trả lời câu này.</p> : question.options.length > 0 ? question.options.map((option) => {
          const percent = answered > 0 ? Math.round((option.count / answered) * 100) : 0;
          return (
            <div key={option.key} className="quiz-results__option">
              <span className={option.correct ? 'quiz-results__option-badge quiz-results__option-badge--correct' : 'quiz-results__option-badge'}>{option.label}</span>
              <span className="quiz-results__option-body">
                <span className="quiz-results__option-head">
                  <span className="quiz-results__option-text">{option.text || /<img\b/i.test(option.html) ? <ExamHtml html={option.html} /> : (option.correct ? 'Đáp án đúng' : '')}</span>
                  <span className="quiz-results__option-count">{option.count} / {answered}</span>
                </span>
                <span className="quiz-results__option-track"><span className={option.correct ? 'quiz-results__option-fill quiz-results__option-fill--correct' : 'quiz-results__option-fill'} style={{ width: `${percent}%` }} /></span>
              </span>
              <span className={option.correct ? 'quiz-results__option-percent quiz-results__option-percent--correct' : 'quiz-results__option-percent'}>{percent}%</span>
            </div>
          );
        }) : (
          <DonutRow slices={[
            { title: 'Đúng', count: question.countCorrectSelections, color: STATUS_COLORS.done },
            { title: 'Sai', count: question.countWrongSelections, color: '#EF4444' },
            { title: 'Bỏ trống', count: question.blank, color: STATUS_COLORS.idle },
          ]} />
        )}
        {question && question.options.length > 0 && question.blank > 0 ? <p className="quiz-results__empty">{question.blank} học sinh bỏ trống câu này.</p> : null}
      </section>
    </>
  );
}
